import { provide } from '@lit/context';
import { localized } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';

import { ConfigurationDto2, ConfigurationService } from '@/api-sdk';
import { FgPrintProof } from '@/components';
import {
  ConfigurationContextType,
  global,
  GlobalComponentType,
  GlobalConfigurationContext,
  initConfigurationContext,
  personalisation,
  PersonalisationType,
  printExample,
  PrintExampleType,
} from '@/context';
import {
  PersonalisationEventController,
  PrintExampleEventController,
} from '@/controllers';
import { rootVariablesStyles } from '@/styles';
import { LayoutType, Mode, Variant } from '@/types';
import { i18nInit, setBaseUrl } from '@/utils';
import { getReducedVariants } from './utils';

import '../loader/fg-loader';
import './fg-configuration-actions';
import './fg-configuration-error';
import './fg-configuration-free-gift';
import './fg-configuration-header';
import './fg-configuration-print-option-list';
import './fg-configuration-quantity-table';
import './fg-configuration-summary';
import './fg-configuration-variant-quantity-table';

import '@/components/print-proof/fg-print-proof';
import '@/components/print-proof/fg-print-proof-status';

@customElement('fg-configuration')
@localized()
export class FgConfiguration extends LitElement {
  @property({ type: String })
  configuration?: string;

  @property({ type: String })
  locale = 'nl_NL';

  @property({ type: String })
  vatRate?: string;

  @property({ type: Boolean })
  configurable = false;

  @property({ type: Boolean })
  deletable = false;

  @property({ type: Boolean })
  hideOrderStatus = false;

  @property({ type: Boolean })
  showPrintProofButton = false;

  @property({ type: Boolean })
  showConfigurationDetails = false;

  @property({ type: Boolean })
  hideConfigurationDetailsToggle = false;

  @property({ type: Boolean })
  hideConfigurationSummary = false;

  @property({ type: Boolean })
  personalizable = false;

  @property({ type: String })
  baseUrl = '';

  @property({ type: String })
  priceBreakdown = '';

  @property({ type: String })
  logoUrl = '';

  @property({ type: Boolean })
  modalInset = false;

  @property({ type: String })
  storeCode?: string;

  /**
   * Property to indicate if the price should be shown in the summary
   * @property showTotals
   */
  @property({ type: Boolean })
  removeTotals = false;

  /**
   * Debtor is the loggedin reseller ID from CE
   *
   * @property {string} debtor
   */
  @property({ type: String })
  debtor: string | undefined = undefined;

  /**
   * Country is the country from the loggedin reseller from CE
   *
   * @property {string} country
   */
  @property({ type: String })
  country: string | undefined = undefined;

  /**
   * Layout type {@link LayoutType}, and is used to render the right theme, components and fetch the right price breakdown data
   *
   * @property {LayoutType} layout
   */
  @property({ type: String })
  layout: LayoutType = 'retail';

  /**
   * validateStock is set when the configuration component needs to check for product stock
   *
   * @property {Number} validateStock
   */
  @property({ type: Number })
  validateStock: 0 | 1 = 0;

  @property({ type: String })
  mode = Mode.DEFAULT;

  /**
   * fullConfiguration of an configuration provided by parent component. If this is filled the configuration request won't be executed.
   *
   * @property {Object} preloadedConfiguration
   */
  @property({ type: Object })
  preloadedConfiguration?: ConfigurationDto2 | null = null;

  @property({ type: String })
  variant = Variant.DEFAULT;

  @state()
  error = false;

  @state()
  isLoading = true;

  @state()
  @provide({ context: global.context })
  globalContext: GlobalComponentType = {
    layout: this.layout,
    locale: this.locale,
    baseUrl: this.baseUrl,
    country: this.country,
    debtor: this.debtor,
  };

  @state()
  configurationData!: ConfigurationDto2;

  @state()
  @provide({ context: GlobalConfigurationContext })
  configurationContext!: ConfigurationContextType;

  @state()
  @provide({ context: personalisation.context })
  personalisationContext: PersonalisationType[] = [];

  @state()
  @provide({ context: printExample.context })
  printExampleContext?: PrintExampleType;

  protected personalisationEventController = new PersonalisationEventController(
    this,
    this.personalisationContext,
  );

  protected printExampleEventController = new PrintExampleEventController(
    this,
    this.printExampleContext,
  );

  fgPrintProofDialogRef: Ref<FgPrintProof> = createRef();

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  async firstUpdated() {
    this.globalContext = {
      layout: this.layout,
      locale: this.locale,
      baseUrl: this.baseUrl,
      country: this.country,
      debtor: this.debtor,
      vatRate: this.vatRate,
    };

    this.configurationContext = initConfigurationContext(this);

    if (this.preloadedConfiguration) {
      this.preloadedConfiguration.printOptions = this.filterPrintOptions(
        this.preloadedConfiguration,
      );
      this.configurationContext.configurationData = this.preloadedConfiguration;
      if (this.preloadedConfiguration?.printExample) {
        printExample.updateProviderEvent(
          this,
          this.preloadedConfiguration.printExample,
        );
      }

      this.isLoading = false;
      return;
    }

    await this.getConfiguration();

    /**
     * For wholesale the stockValidationSucceeded is set to false when the stock validation fails.
     * When stockValidationSucceeded failed show the configuration details.
     */
    if (!this.configurationContext.configurationData) {
      return null;
    }
    const { stockValidationSucceeded } =
      this.configurationContext.configurationData;

    if (stockValidationSucceeded === false) {
      this.configurationContext = {
        ...this.configurationContext,
        showConfigurationDetails: true,
      };
    }
  }

  filterPrintOptions(configuration: ConfigurationDto2) {
    return configuration.printOptions.filter(
      printOption => !printOption.hidden,
    );
  }

  willUpdate() {
    this.personalisationContext = this.personalisationEventController.context;
    this.printExampleContext = this.printExampleEventController.context;
  }

  async getConfiguration() {
    const { locale, vatRate, debtor, country } = this.globalContext;
    const { configurationId, validateStock } = this.configurationContext;

    try {
      const configuration = await ConfigurationService.getShowConfiguration({
        uuid: configurationId,
        locale: locale,
        vatRate: vatRate,
        debtor: debtor,
        country: country,
        validateStock: validateStock,
      });
      if (configuration) {
        configuration.printOptions = this.filterPrintOptions(configuration);

        if (configuration.printExample) {
          printExample.updateProviderEvent(this, configuration.printExample);
        }

        this.configurationContext = {
          ...this.configurationContext,
          configurationData: configuration,
        };

        this.isLoading = false;
      }
    } catch {
      this.isLoading = false;
      this.error = true;
    }
  }

  render() {
    if (this.isLoading) {
      return html`
        <fg-loader
          type="configuration"
          .bgcolor=${this.variant === Variant.SIMPLE
            ? 'transparent'
            : '#ffffff'}
        ></fg-loader>
      `;
    }

    if (this.error) {
      return html`
        <fg-configuration-actions></fg-configuration-actions>

        <fg-configuration-error></fg-configuration-error>
      `;
    }

    if (!this.configurationContext.configurationData) {
      return null;
    }

    const {
        printOptions,
        printProof,
        product,
        variants,
        orderStatus,
        freeGift,
        worth,
      } = this.configurationContext.configurationData,
      reducedVariants = getReducedVariants(variants),
      printExampleData = {
        configurationId: this.configuration || undefined,
        data: this.printExampleContext,
      },
      { layout } = this.globalContext,
      {
        modalInset,
        showConfigurationDetails,
        showPrintProofButton,
        configurationId,
        personalizable,
        logoUrl,
        storeCode,
      } = this.configurationContext;

    switch (freeGift) {
      case true:
        return html`
          <fg-configuration-free-gift
            .product=${product}
            .worth=${worth}
            .variant=${variants}
            .reducedVariants=${reducedVariants}
          ></fg-configuration-free-gift>
        `;
      default:
        return html`
          ${modalInset
            ? nothing
            : html`
                <fg-configuration-actions
                  part=${layout}
                  toggleable
                  @details-toggled=${this.toggleShowConfigurationDetails}
                ></fg-configuration-actions>
              `}
          <fg-configuration-header
            .product=${product}
            .printOptionsLength=${printOptions.length}
            .reducedVariants=${reducedVariants}
            .orderStatus=${this.hideOrderStatus ? null : orderStatus}
          ></fg-configuration-header>

          <fg-print-proof
            part=${layout}
            ${ref(this.fgPrintProofDialogRef)}
            .configurationId=${this.configuration || ''}
            .logoUrl=${logoUrl}
            .printProof=${printProof}
            .storeCode=${storeCode}
            @print-proof-review-submitted=${this.getConfiguration}
          ></fg-print-proof>

          ${printProof
            ? html`
                <fg-print-proof-status
                  .review=${printProof.review}
                  .status=${printProof.status}
                  @open-print-proof-dialog=${this.openPrintProofDialog}
                ></fg-print-proof-status>
              `
            : nothing}
          <div class="configuration-details">
            ${showConfigurationDetails
              ? html`
                  <fg-configuration-print-option-list
                    part=${layout}
                    .printOptions=${printOptions}
                    @personalisation-data-saved=${this.getConfiguration}
                  ></fg-configuration-print-option-list>

                  ${layout === 'retail'
                    ? html`
                        <fg-configuration-quantity-table
                          .variants=${variants}
                          .reducedVariants=${reducedVariants}
                        ></fg-configuration-quantity-table>
                      `
                    : html`
                        <fg-configuration-variant-quantity-table
                          .product=${product}
                          .variants=${variants}
                          .reducedVariants=${reducedVariants}
                        ></fg-configuration-variant-quantity-table>
                        ${printOptions.length > 0 && printExampleData
                          ? html`
                              <fg-print-example
                                .configurationId=${configurationId}
                                .productName=${product.name}
                                .personalizable=${personalizable}
                                .printExampleData=${printExampleData ||
                                undefined}
                                .showPrintProofButton=${showPrintProofButton}
                              ></fg-print-example>
                            `
                          : nothing}
                      `}
                `
              : nothing}
          </div>

          ${this.hideConfigurationSummary && this.variant === Variant.DEFAULT
            ? nothing
            : html`
                <fg-configuration-summary
                  .removeTotals=${this.removeTotals}
                  ?hideConfigurationDetailsToggle=${this
                    .hideConfigurationDetailsToggle}
                  part=${layout}
                  toggleable
                  @details-toggled=${this.toggleShowConfigurationDetails}
                ></fg-configuration-summary>
              `}
        `;
    }
  }

  toggleShowConfigurationDetails() {
    this.configurationContext = {
      ...this.configurationContext,
      showConfigurationDetails:
        !this.configurationContext.showConfigurationDetails,
    };
  }

  openPrintProofDialog() {
    this.fgPrintProofDialogRef.value?.openDialog();
  }

  static styles = [
    rootVariablesStyles,
    css`
      .configuration-details {
        display: flex;
        flex-direction: column;
      }

      :host([part='retail'])
        .configuration-details
        fg-configuration-print-option-list {
        order: 1;
      }
      :host([part='retail'])
        .configuration-details
        fg-configuration-quantity-table {
        order: 2;
      }

      :host([part='wholesale']) fg-configuration-summary {
        margin-top: 16px;
      }
      :host([part='wholesale'])
        .configuration-details
        fg-configuration-print-option-list {
        order: 2;
      }
      :host([part='wholesale']) .configuration-details fg-print-example {
        order: 3;
      }
      :host([part='wholesale'])
        .configuration-details
        fg-configuration-quantity-table {
        order: 1;
      }
    `,
  ];
}
